import {
  isTextual,
  nextZoomStep,
  screenToBoard,
  zoomAt,
  type NodeId,
} from '@kanvas/board-core';
import { useEffect } from 'react';
import {
  deleteSelection,
  duplicateSelection,
  groupSelection,
  redo,
  reorderSelection,
  selectAll,
  setLocked,
  translateNodes,
  ungroupSelection,
  undo,
  zoomToFit,
  zoomToSelection,
  type ActionContext,
} from '../actions/actions';
import { copySelection, cutSelection, paste } from '../actions/clipboard';
import { exportBoard } from '../export/exportBoard';
import { applyImageCrop, cancelImageCrop } from '../interaction/gestures';
import { useEditor, type ToolId } from '../state/store';

/**
 * Tools keyed by physical key position (`KeyboardEvent.code`), not by the
 * character produced (`KeyboardEvent.key`).
 *
 * This is the difference between shortcuts that work and shortcuts that are
 * dead: with a Cyrillic layout active, the key labelled V reports
 * `key === 'в'`, so every latin comparison fails and the whole map stops
 * responding — Ctrl+Z, Ctrl+C and Ctrl+V included. `code` describes where the
 * key physically is, so the same keys work on any layout.
 */
const TOOL_KEYS: Record<string, ToolId> = {
  KeyV: 'select',
  Digit1: 'select',
  KeyH: 'hand',
  KeyN: 'sticky',
  KeyS: 'sticky',
  KeyR: 'shape',
  KeyO: 'shape',
  KeyT: 'text',
  KeyP: 'draw',
  KeyD: 'draw',
  KeyE: 'eraser',
  KeyL: 'connector',
  KeyA: 'connector',
  KeyF: 'frame',
  KeyC: 'comment',
  KeyK: 'laser',
  KeyB: 'notebook',
};

/**
 * Global keyboard map.
 *
 * Bound on `window` rather than on the canvas so shortcuts work when focus sits
 * on a panel button. Text being typed is left alone — see `isTyping`.
 */
export function useShortcuts(ctx: ActionContext, options: { onUploadImage?: () => void } = {}) {
  const { onUploadImage } = options;

  useEffect(() => {
    let pasteEventSeen = false;
    let pasteFallbackTimer = 0;

    const onKeyDown = (e: KeyboardEvent) => {
      const primary = isMacPlatform() ? e.metaKey : e.ctrlKey;
      if (isTyping(e.target, primary || e.altKey)) return;

      const editor = useEditor.getState();
      const key = e.key;
      const code = e.code;

      // The open notebook owns the keyboard (paste, delete, arrows, zoom,
      // Escape). Board shortcuts would otherwise act on the hidden canvas —
      // deleting the board selection or pasting onto the board. Only history
      // stays global, since notebook edits live in the same undo stack.
      if (editor.enteredNotebookId) {
        if (primary && code === 'KeyZ') {
          e.preventDefault();
          if (e.shiftKey) redo(ctx);
          else undo(ctx);
        } else if (primary && code === 'KeyY') {
          e.preventDefault();
          redo(ctx);
        }
        return;
      }

      // ---- editing / selection ------------------------------------------
      if (key === 'Escape') {
        // Follow mode is the most disorienting state to be stuck in, so it is
        // the first thing Escape releases.
        if (editor.followingUserId) editor.setUi({ followingUserId: null });
        else if (editor.croppingId) cancelImageCrop();
        else if (editor.editingId) editor.setEditing(null);
        else if (editor.enteredGroups.length > 0) editor.exitGroups();
        else editor.clearSelection();
        return;
      }

      if (key === 'Enter' && editor.croppingId && !primary) {
        e.preventDefault();
        applyImageCrop({ doc: ctx.doc });
        return;
      }

      if ((key === 'Delete' || key === 'Backspace') && !primary) {
        e.preventDefault();
        deleteSelection(ctx);
        return;
      }

      if (key === 'Enter' && !primary && editor.selection.length === 1) {
        const node = ctx.doc.get(editor.selection[0]!);
        if (node && isTextual(node) && !node.locked) {
          e.preventDefault();
          editor.setEditing(node.id);
        }
        return;
      }

      if (key === 'Tab' && editor.selection.length > 0) {
        // Cycle selection through siblings in paint order.
        e.preventDefault();
        const all = ctx.doc.all().filter((n) => n.type !== 'group' && !n.locked);
        if (all.length === 0) return;
        const currentIndex = all.findIndex((n) => n.id === editor.selection[0]);
        const step = e.shiftKey ? -1 : 1;
        const next = all[(currentIndex + step + all.length) % all.length]!;
        editor.select([next.id]);
        return;
      }

      // ---- clipboard ------------------------------------------------------
      // Copy and cut are handled by the `copy` / `cut` events below, which
      // can write the clipboard synchronously; doing it here as well raced
      // with them.
      if (primary && (code === 'KeyC' || code === 'KeyX')) return;
      if (primary && code === 'KeyV') {
        /*
         * The `paste` event handler below is the preferred path, because only
         * it carries clipboard data the browser will hand over without a
         * permission prompt. It does not fire in every situation though, so
         * this branch falls back to the async clipboard API — but only when
         * no paste event arrived, otherwise everything was pasted twice.
         */
        pasteEventSeen = false;
        window.clearTimeout(pasteFallbackTimer);
        pasteFallbackTimer = window.setTimeout(() => {
          if (pasteEventSeen) return;
          const vp = useEditor.getState().viewport;
          void paste(ctx, screenToBoard(vp, { x: vp.width / 2, y: vp.height / 2 }));
        }, 60);
        return;
      }
      if (primary && code === 'KeyD') {
        e.preventDefault();
        duplicateSelection(ctx);
        return;
      }

      // ---- history --------------------------------------------------------
      if (primary && code === 'KeyZ') {
        e.preventDefault();
        if (e.shiftKey) redo(ctx);
        else undo(ctx);
        return;
      }
      if (primary && code === 'KeyY') {
        e.preventDefault();
        redo(ctx);
        return;
      }

      // ---- structure ------------------------------------------------------
      if (primary && code === 'KeyA') {
        e.preventDefault();
        selectAll(ctx);
        return;
      }
      if (primary && code === 'KeyG') {
        e.preventDefault();
        if (e.shiftKey) ungroupSelection(ctx);
        else groupSelection(ctx);
        return;
      }
      if (primary && code === 'KeyL') {
        e.preventDefault();
        const locked = ctx.doc.getMany(editor.selection).some((n) => n.locked);
        setLocked(ctx, !locked);
        return;
      }
      if (primary && code === 'KeyS') {
        e.preventDefault();
        void exportBoard(ctx.doc, 'json');
        return;
      }
      if (primary && code === 'KeyU') {
        e.preventDefault();
        onUploadImage?.();
        return;
      }

      // ---- z-order --------------------------------------------------------
      if (code === 'BracketRight') {
        e.preventDefault();
        reorderSelection(ctx, primary ? 'forward' : 'front');
        return;
      }
      if (code === 'BracketLeft') {
        e.preventDefault();
        reorderSelection(ctx, primary ? 'backward' : 'back');
        return;
      }

      // ---- camera ---------------------------------------------------------
      if (
        primary &&
        (code === 'Equal' ||
          code === 'Minus' ||
          code === 'NumpadAdd' ||
          code === 'NumpadSubtract')
      ) {
        e.preventDefault();
        const direction = code === 'Minus' || code === 'NumpadSubtract' ? -1 : 1;
        useEditor.getState().setViewport((vp) =>
          zoomAt(vp, nextZoomStep(vp.zoom, direction), { x: vp.width / 2, y: vp.height / 2 }),
        );
        return;
      }
      if (primary && (code === 'Digit0' || code === 'Numpad0')) {
        e.preventDefault();
        useEditor
          .getState()
          .setViewport((vp) => zoomAt(vp, 1, { x: vp.width / 2, y: vp.height / 2 }));
        return;
      }
      if (e.shiftKey && !primary && code === 'Digit1') {
        e.preventDefault();
        zoomToFit(ctx);
        return;
      }
      if (e.shiftKey && !primary && code === 'Digit2') {
        e.preventDefault();
        zoomToSelection(ctx);
        return;
      }

      // ---- nudge ----------------------------------------------------------
      if (key.startsWith('Arrow') && editor.selection.length > 0) {
        e.preventDefault();
        const meta = ctx.doc.getMeta();
        const base = meta.snapToGrid ? meta.gridSize : 1;
        const step = e.shiftKey ? base * 10 : base;
        const dx = key === 'ArrowLeft' ? -step : key === 'ArrowRight' ? step : 0;
        const dy = key === 'ArrowUp' ? -step : key === 'ArrowDown' ? step : 0;
        nudge(ctx, editor.selection, dx, dy);
        return;
      }

      // ---- tools ----------------------------------------------------------
      if (!primary && !e.altKey && !e.shiftKey && TOOL_KEYS[code]) {
        editor.setTool(TOOL_KEYS[code]!);
      }
    };

    const onPaste = (e: ClipboardEvent) => {
      pasteEventSeen = true;
      if (isTyping(e.target, false)) return;
      if (useEditor.getState().enteredNotebookId) return;
      e.preventDefault();
      const vp = useEditor.getState().viewport;
      // Paste lands at the centre of the current view, which is predictable
      // whatever the pointer happens to be doing.
      const at = screenToBoard(vp, { x: vp.width / 2, y: vp.height / 2 });
      void paste(ctx, at, e);
    };

    const onCopy = (e: ClipboardEvent) => {
      if (isTyping(e.target, false)) return;
      if (useEditor.getState().enteredNotebookId) return;
      if (useEditor.getState().selection.length === 0) return;
      e.preventDefault();
      void copySelection(ctx, e);
    };

    const onCut = (e: ClipboardEvent) => {
      if (isTyping(e.target, false)) return;
      if (useEditor.getState().enteredNotebookId) return;
      if (useEditor.getState().selection.length === 0) return;
      e.preventDefault();
      void cutSelection(ctx, e);
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('paste', onPaste);
    window.addEventListener('copy', onCopy);
    window.addEventListener('cut', onCut);
    return () => {
      window.removeEventListener('cut', onCut);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('paste', onPaste);
      window.clearTimeout(pasteFallbackTimer);
      window.removeEventListener('copy', onCopy);
    };
    // `onUploadImage` is destructured rather than depending on the whole
    // options object: a caller passing an inline literal would otherwise
    // re-subscribe these listeners on every render.
  }, [ctx, onUploadImage]);
}

function nudge(ctx: ActionContext, ids: readonly NodeId[], dx: number, dy: number): void {
  if (dx === 0 && dy === 0) return;
  const delta = { x: dx, y: dy };
  const patches = translateNodes(
    ctx.doc,
    ctx.doc.getMany(ids).map((node) => ({ node, delta })),
  );
  if (patches.size > 0) ctx.doc.updateMany(patches);
}

/** Input types that hold text the user is editing. */
const TEXT_INPUT_TYPES = new Set([
  'text',
  'search',
  'email',
  'url',
  'password',
  'tel',
  'date',
  'time',
]);

/**
 * Whether a key event belongs to whatever has focus rather than to the board.
 *
 * The inline text editor and real text fields always win. Numeric fields in
 * the style panel are the exception: plain keystrokes stay with the control so
 * digits type normally, but modifier chords pass through to the board. A user
 * who adjusted a stroke width and then pressed Ctrl+Z means "undo my last
 * board edit", not "revert this spinner" — and before this distinction
 * existed, clicking a stepper silently killed every shortcut until they
 * clicked back onto the canvas.
 */
function isTyping(target: EventTarget | null, hasModifier: boolean): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;

  const tag = target.tagName;
  if (tag === 'TEXTAREA') return true;
  if (tag !== 'INPUT') return false;

  const type = (target as HTMLInputElement).type.toLowerCase();
  if (TEXT_INPUT_TYPES.has(type)) return true;
  // number, range, checkbox, colour: only unmodified keys belong to them.
  return !hasModifier;
}

function isMacPlatform(): boolean {
  const nav = navigator as Navigator & { userAgentData?: { platform?: string } };
  const platform = nav.userAgentData?.platform ?? navigator.platform ?? '';
  return /mac/i.test(platform);
}
